/**
 * Workflow tracking / reporting: a combined bottleneck (aging) view plus drill-down
 * audit trail, in one page. Read-only for everyone who can reach it (RequireWorkflowAccess
 * in App.jsx allows admin, superadmin, the read-only workflow_viewer role, and the
 * reminder_sender role) — no destructive actions live here for anyone, including admins
 * (those stay on MatterDetail.jsx). The one deliberate exception is the scoped,
 * non-destructive manual reminder notification on a currently-pending approval step.
 *
 * "Current step" and "days in current step" are derived the same way MatterDetail.jsx
 * already renders the approval chain: the approvalSteps subcollection ordered by
 * `order`, with the active one being the first step in `pending` or `info_requested`
 * status (see approval/engine.js's activateGroup, which is what sets `activatedAt` when
 * a step becomes the active one). A matter with no such step is either still a draft
 * (no steps yet), fully approved, or rejected.
 */

import { useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../auth/AuthProvider';
import { functions } from '../firebase';
import { useCollection } from '../lib/useFirestore';
import StatusBadge from '../components/StatusBadge';
import { formatTs } from './MatterList';
import { canRemindPendingStep, displayUserName } from '../lib/workflowTracking';

const ACTIVE_STEP_STATUSES = ['pending', 'info_requested'];

function msToDays(ms) {
  return ms / (1000 * 60 * 60 * 24);
}

/** The step this matter is currently waiting on, or null if none is active. */
function currentStepOf(steps) {
  const active = steps
    .filter((s) => ACTIVE_STEP_STATUSES.includes(s.status))
    .sort((a, b) => (a.order || 0) - (b.order || 0));
  return active[0] || null;
}

function daysInCurrentStep(step, now) {
  if (!step || !step.activatedAt) return null;
  const activatedAt = step.activatedAt.toDate ? step.activatedAt.toDate() : new Date(step.activatedAt);
  return msToDays(now - activatedAt);
}

const statCard = 'rounded-lg bg-white dark:bg-slate-900 p-4 shadow';

export default function WorkflowTracking() {
  const { isAdmin, isReminderSender } = useAuth();
  const matters = useCollection('matters', [['orderBy', 'updatedAt', 'desc']]);
  const roster = useCollection('roster');
  const practiceGroups = useCollection('practiceGroups');
  const [expanded, setExpanded] = useState(null);

  const [statusFilter, setStatusFilter] = useState('');
  const [practiceGroupFilter, setPracticeGroupFilter] = useState('');
  const [minDaysStuck, setMinDaysStuck] = useState('');
  const [search, setSearch] = useState('');

  const names = useMemo(
    () => Object.fromEntries(roster.data.map((u) => [u.id, u.displayName || u.email])),
    [roster.data]
  );
  const groupNames = useMemo(
    () => Object.fromEntries(practiceGroups.data.map((g) => [g.id, g.name || g.id])),
    [practiceGroups.data]
  );

  // Every matter needs its approvalSteps subcollection to compute a current step, but
  // useCollection only reads one path at a time, so this page fetches them per-matter
  // via a single flat query is not possible with the shared hook — instead each row
  // resolves its own steps with a small subcomponent (StepAwareRow), keeping this
  // component itself a straightforward list/filter/aggregate.
  const rows = matters.data;

  const filtered = useMemo(() => {
    return rows.filter((m) => {
      if (statusFilter && m.status !== statusFilter) return false;
      if (practiceGroupFilter && m.header?.practiceGroupId !== practiceGroupFilter) return false;
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        const owner = displayUserName(names, m.createdBy, '');
        const hay = `${m.client?.name || ''} ${m.filesToBeOpened?.matterName || ''} ${owner}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, statusFilter, practiceGroupFilter, search, names]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-firm-700 dark:text-firm-100">Workflow tracking</h1>
      </div>
      <p className="text-sm text-slate-600 dark:text-slate-300">
        Read-only view of every matter's current status, current approval step, how long
        it has been stuck there, and its full audit trail. Sorted with the
        longest-waiting matters first so bottlenecks surface immediately.
      </p>

      <StatsAndTable
        matters={filtered}
        allMatters={rows}
        names={names}
        groupNames={groupNames}
        practiceGroups={practiceGroups.data}
        expanded={expanded}
        setExpanded={setExpanded}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        practiceGroupFilter={practiceGroupFilter}
        setPracticeGroupFilter={setPracticeGroupFilter}
        minDaysStuck={minDaysStuck}
        setMinDaysStuck={setMinDaysStuck}
        search={search}
        setSearch={setSearch}
        loading={matters.loading}
        canSendReminder={isAdmin || isReminderSender}
      />
    </div>
  );
}

/**
 * Wraps the table body in its own component so each row can resolve its own steps
 * subcollection (via StepAwareRow) while the aggregate stats and the min-days-stuck
 * filter — which depend on a step-derived value that only exists once a row's own
 * subcollection has loaded — are computed here from those same per-row results.
 */
function StatsAndTable({
  matters,
  names,
  groupNames,
  practiceGroups,
  expanded,
  setExpanded,
  statusFilter,
  setStatusFilter,
  practiceGroupFilter,
  setPracticeGroupFilter,
  minDaysStuck,
  setMinDaysStuck,
  search,
  setSearch,
  loading,
  canSendReminder,
}) {
  const [rowInfo, setRowInfo] = useState({}); // matterId -> { step, days, stepsLoaded }

  const reportRowInfo = (matterId, info) =>
    setRowInfo((prev) => {
      const existing = prev[matterId];
      if (
        existing &&
        existing.days === info.days &&
        existing.step?.id === info.step?.id &&
        existing.stepsLoaded === info.stepsLoaded
      ) {
        return prev;
      }
      return { ...prev, [matterId]: info };
    });

  const minDays = minDaysStuck === '' ? null : Number(minDaysStuck);

  const visibleMatters = matters.filter((m) => {
    if (minDays == null) return true;
    const info = rowInfo[m.id];
    if (!info || info.days == null) return false;
    return info.days >= minDays;
  });

  const sorted = [...visibleMatters].sort((a, b) => {
    const da = rowInfo[a.id]?.days ?? -1;
    const db = rowInfo[b.id]?.days ?? -1;
    return db - da;
  });

  const stuckOver3 = matters.filter((m) => (rowInfo[m.id]?.days ?? 0) > 3).length;
  const stuckOver7 = matters.filter((m) => (rowInfo[m.id]?.days ?? 0) > 7).length;
  const openCount = matters.filter((m) => ['draft', 'pending_approval'].includes(m.status)).length;

  const byStepLabel = {};
  matters.forEach((m) => {
    const label = rowInfo[m.id]?.step?.label;
    if (!label) return;
    byStepLabel[label] = (byStepLabel[label] || 0) + 1;
  });

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-4">
        <div className={statCard}>
          <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Open matters</p>
          <p className="mt-1 text-2xl font-semibold text-slate-800 dark:text-slate-100">{openCount}</p>
        </div>
        <div className={statCard}>
          <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Stuck &gt; 3 days</p>
          <p className="mt-1 text-2xl font-semibold text-amber-700 dark:text-amber-300">{stuckOver3}</p>
        </div>
        <div className={statCard}>
          <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Stuck &gt; 7 days</p>
          <p className="mt-1 text-2xl font-semibold text-red-700 dark:text-red-300">{stuckOver7}</p>
        </div>
        <div className={statCard}>
          <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">By current step</p>
          <div className="mt-1 space-y-0.5">
            {Object.entries(byStepLabel).length === 0 ? (
              <p className="text-xs text-slate-400 dark:text-slate-500">Nothing active</p>
            ) : (
              Object.entries(byStepLabel).map(([label, count]) => (
                <p key={label} className="text-xs text-slate-600 dark:text-slate-300">
                  {label}: <span className="font-medium text-slate-800 dark:text-slate-100">{count}</span>
                </p>
              ))
            )}
          </div>
        </div>
      </div>

      <section className="flex flex-wrap items-end gap-3 rounded-lg bg-white dark:bg-slate-900 p-4 shadow">
        <label className="text-xs text-slate-600 dark:text-slate-300">
          Status
          <select
            className="mt-1 block rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All</option>
            <option value="draft">Draft</option>
            <option value="pending_approval">Pending approval</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="on_hold">On hold</option>
            <option value="recalled">Recalled</option>
          </select>
        </label>
        <label className="text-xs text-slate-600 dark:text-slate-300">
          Practice group
          <select
            className="mt-1 block rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm"
            value={practiceGroupFilter}
            onChange={(e) => setPracticeGroupFilter(e.target.value)}
          >
            <option value="">All</option>
            {practiceGroups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name || g.id}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-slate-600 dark:text-slate-300">
          Minimum days stuck
          <input
            type="number"
            min="0"
            className="mt-1 block w-28 rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm"
            placeholder="e.g. 3"
            value={minDaysStuck}
            onChange={(e) => setMinDaysStuck(e.target.value)}
          />
        </label>
        <label className="text-xs text-slate-600 dark:text-slate-300">
          Search client / matter name
          <input
            type="text"
            className="mt-1 block w-56 rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm"
            placeholder="Search…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
      </section>

      <section className="rounded-lg bg-white dark:bg-slate-900 shadow">
        {loading ? (
          <p className="p-4 text-sm text-slate-500 dark:text-slate-400">Loading…</p>
        ) : sorted.length === 0 ? (
          <p className="p-4 text-sm text-slate-500 dark:text-slate-400">No matters match these filters.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800 text-left text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
              <tr>
                <th className="px-4 py-2">Client / matter</th>
                <th className="px-4 py-2">Owner</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Current step</th>
                <th className="px-4 py-2">Days in step</th>
                <th className="px-4 py-2">Approver(s)</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {sorted.map((matter) => (
                <StepAwareRow
                  key={matter.id}
                  matter={matter}
                  names={names}
                  groupNames={groupNames}
                  expanded={expanded === matter.id}
                  onToggle={() => setExpanded(expanded === matter.id ? null : matter.id)}
                  onStepInfo={(info) => reportRowInfo(matter.id, info)}
                  canSendReminder={canSendReminder}
                />
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

/** One matter's row, plus its own approvalSteps/auditLog subcollections. */
function StepAwareRow({ matter, names, groupNames, expanded, onToggle, onStepInfo, canSendReminder }) {
  const steps = useCollection(`matters/${matter.id}/approvalSteps`, [['orderBy', 'order', 'asc']]);
  const audit = useCollection(`matters/${matter.id}/auditLog`, [['orderBy', 'at', 'desc']]);
  const [reminderState, setReminderState] = useState('idle'); // idle | sending | sent | error
  const [reminderError, setReminderError] = useState('');

  const step = currentStepOf(steps.data);
  const days = daysInCurrentStep(step, new Date());

  // Report this row's derived info up to the parent for aggregate stats/sorting/filter.
  // reportRowInfo (passed down as onStepInfo) already no-ops when nothing changed, so
  // this does not cause a render loop.
  useEffect(() => {
    onStepInfo({ step, days, stepsLoaded: !steps.loading });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step?.id, days, steps.loading]);

  const approverNames = (step?.eligibleApproverUids || [])
    .map((uid) => displayUserName(names, uid))
    .join(', ');
  const canRemindThisStep = canRemindPendingStep({
    canSendReminder,
    matterStatus: matter.status,
    stepStatus: step?.status,
  });

  const sendReminder = async () => {
    if (!canRemindThisStep) return;
    setReminderState('sending');
    setReminderError('');
    try {
      await httpsCallable(functions, 'sendManualReminder')({ matterId: matter.id, stepId: step.id });
      setReminderState('sent');
    } catch (err) {
      setReminderState('error');
      setReminderError(err.message || 'Could not send the reminder.');
    }
  };

  return (
    <>
      <tr>
        <td className="px-4 py-2">
          <div className="font-medium text-slate-800 dark:text-slate-100">
            {matter.client?.name || <em className="text-slate-400 dark:text-slate-500">Untitled</em>}
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400">{matter.filesToBeOpened?.matterName || '—'}</div>
          <div className="text-xs text-slate-400 dark:text-slate-500">
            {groupNames[matter.header?.practiceGroupId] || 'Unassigned'}
          </div>
        </td>
        <td className="px-4 py-2 text-slate-700 dark:text-slate-200">
          {displayUserName(names, matter.createdBy)}
        </td>
        <td className="px-4 py-2">
          <StatusBadge status={matter.status} />
        </td>
        <td className="px-4 py-2 text-slate-700 dark:text-slate-200">{step ? step.label : '—'}</td>
        <td className="px-4 py-2 text-slate-700 dark:text-slate-200">
          {days == null ? '—' : `${Math.floor(days)} day${Math.floor(days) === 1 ? '' : 's'}`}
        </td>
        <td className="px-4 py-2 text-xs text-slate-600 dark:text-slate-300">{approverNames || '—'}</td>
        <td className="px-4 py-2 text-right">
          <div className="flex flex-col items-end gap-1">
            <div className="flex items-center justify-end gap-2">
              {canRemindThisStep && (
                <button
                  type="button"
                  onClick={sendReminder}
                  disabled={reminderState === 'sending'}
                  className="text-xs font-medium text-firm-700 dark:text-firm-100 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                  title={`Send a reminder to ${approverNames || 'the current approver'}`}
                >
                  {reminderState === 'sending' ? 'Sending…' : 'Remind'}
                </button>
              )}
              <button type="button" onClick={onToggle} className="text-xs text-firm-700 dark:text-firm-100 hover:underline">
                {expanded ? 'Hide audit trail' : 'View audit trail'}
              </button>
            </div>
            <span aria-live="polite" className="max-w-56 text-right text-xs">
              {reminderState === 'sent' && (
                <span className="text-emerald-600 dark:text-emerald-400">Reminder queued.</span>
              )}
              {reminderState === 'error' && (
                <span className="text-red-600 dark:text-red-400">{reminderError}</span>
              )}
            </span>
          </div>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={7} className="bg-slate-50 dark:bg-slate-800 px-4 py-3">
            <AuditTrail steps={steps.data} audit={audit.data} names={names} />
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Full drill-down audit trail for one matter: every approval step with status,
 * approver, timestamp, and comments, plus the raw audit log — the same underlying
 * data MatterDetail.jsx renders, reused here read-only (no admin override controls).
 */
function AuditTrail({ steps, audit, names }) {
  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Approval chain
        </h3>
                  <ol className="mt-2 divide-y divide-slate-200 dark:divide-slate-700">
          {steps.map((step) => (
            <li key={step.id} className="py-2">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{step.label}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {(step.eligibleApproverUids || []).map((uid) => displayUserName(names, uid)).join(' / ') ||
                      'Unresolved — escalated'}
                  </p>
                  {(step.comments || []).map((c, i) => (
                    <p key={i} className="mt-1 rounded bg-white dark:bg-slate-900 px-2 py-1 text-xs text-slate-700 dark:text-slate-200">
                      <strong>{displayUserName(names, c.by || c.uid, 'Unknown user', c.byName)}:</strong> {c.text}
                    </p>
                  ))}
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <StatusBadge status={step.status} />
                  {step.decidedAt && (
                    <span className="text-xs text-slate-400 dark:text-slate-500">{formatTs(step.decidedAt)}</span>
                  )}
                </div>
              </div>
            </li>
          ))}
          {steps.length === 0 && <li className="py-2 text-sm text-slate-500 dark:text-slate-400">No steps yet.</li>}
        </ol>
      </div>

      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">History</h3>
        <ol className="mt-2 space-y-2">
          {audit.map((entry) => (
            <li key={entry.id} className="flex gap-3 text-sm">
              <span className="w-40 shrink-0 text-xs text-slate-400 dark:text-slate-500">{formatTs(entry.at)}</span>
              <span className="flex-1">
                <span className="text-slate-800 dark:text-slate-100">{entry.summary}</span>
                <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">
                  {entry.actor?.displayName || displayUserName(names, entry.actor?.uid, 'system')}
                  {entry.actor?.via && ` · via ${entry.actor.via}`}
                </span>
              </span>
            </li>
          ))}
          {audit.length === 0 && <li className="text-sm text-slate-500 dark:text-slate-400">Nothing yet.</li>}
        </ol>
      </div>
    </div>
  );
}
